import React, { ChangeEvent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import IForm from '../../types/IForm';
import ContentMessage from '../../_lib/components/ContentMessage/ContentMessage';
import { Namespace } from '../../translation/namespace';
import Form, { FormTemplate } from '../../components/molecules/Form/Form';
import LiveRegion from '../../components/atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../types/InteractionStatus';
import { FormUtils } from '../../components/molecules/Form';
import FormsRow from '../../components/molecules/Form/components/FormsRow/FormsRow';
import FormSelect, { optionsProcessor } from '../../components/molecules/Form/components/FormSelect/FormSelect';
import { asFetchableBlock, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { FormSubmitButton } from '../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';

type UnavailableNewsletterSettings = { unavailableReason: string };
type AvailableNewsletterSettings = { newsletterSettingsForm: IForm };
type NewsletterSettingsProps = UnavailableNewsletterSettings | AvailableNewsletterSettings;

function isUnavailable(props: NewsletterSettingsProps): props is UnavailableNewsletterSettings {
  return !!(props as UnavailableNewsletterSettings).unavailableReason;
}
function NewsletterSettings(props: NewsletterSettingsProps) {
  const { t } = useTranslation(Namespace.NewsletterSettingsForm);
  const { refresh } = useRefreshBlock();
  return isUnavailable(props)
    ? (
        <ContentMessage text={t(`unavailableReasons.noEmailAddress`)} />
      )
    : (
        <Form
          id="newsletter-settings-save-form"
          model={props.newsletterSettingsForm}
          onSubmit={(e: Event, model: any) => {
            e.preventDefault();
            model
              .submit()
              .then(() => setTimeout(refresh, 2000))
              .catch(() => {
                /* ignore */
              });
          }}
        >
          <FormTemplate>
            {({ inputs, onChange, hasChanges, getStatus }: any) => (
              <>
                <FormsRow>
                  <FormSelect
                    id={`${inputs.frequency.name}-newslettersettings`}
                    name={inputs.frequency.name}
                    required={inputs.frequency.required}
                    labelTitle={t('inputs.frequency.label')}
                    value={inputs.frequency.value}
                    options={optionsProcessor(
                      inputs.frequency.name,
                      inputs.frequency.options,
                      Namespace.NewsletterSettingsForm,
                    )}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.name, event.target.value)}
                  />
                </FormsRow>
                <FormSubmitButton buttonLabel={t('saveSettings')} enabled={hasChanges()} />
                <LiveRegion htmlTag="div" invisible={false} ariaAtomic ariaLive="polite">
                  {getStatus({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('success'), InteractionStatus.SUCCESS),
                  })}
                </LiveRegion>
              </>
            )}
          </FormTemplate>
        </Form>
      );
}

export const NewsletterSettingsBlock = asFetchableBlock(NewsletterSettings, BlockType.newslettersettings);
export type NewsletterSettingsBlock = ComponentProps<typeof NewsletterSettingsBlock>;
